/* ═══════════════════════════════════════════════════════════════
   THS3 CHOREOGRAPHY DIRECTOR — pairs with assets/ths3-choreo.css
   Created 2026-08-06.

   Replaces four competing motion systems (see the CSS header) with
   ONE director that gives every section a single trigger and then
   deliberately offsets what happens inside it:

     banner → eyebrow → heading (word by word) → the rule drawing
     itself → copy → CTA, with the cards arriving on a diagonal
     underneath and the photography still settling out of its zoom
     after the card has already landed.

   Two things make that read as one continuous gesture rather than
   a queue of separate animations:

     1. OVERLAP. Each stage starts before the previous one has
        finished. Nothing waits its turn.
     2. EARLY HAND-OFF. The section trigger fires while the section
        is still mostly below the fold, so the next section's
        heading is already moving as the previous section's last
        row settles. There is no dead moment between sections —
        which is what "the scroll feels alive" means mechanically.

   NOTHING HERE IS LOAD-BEARING. Elements are only ever hidden by
   the .rv class, every one of them is either revealed on entry or
   caught by the sweep below, and anything already on screen at
   first paint is handled without ever being hidden. A page must
   never open on blank bands.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  if (designMode) root.classList.add('ths3-editor');

  var reduced = designMode || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* ── the cast ─────────────────────────────────────────────────
     LEAD     the section's own voice: eyebrow, rule, heading, copy,
              CTA row, filter chips.
     MEDIA    editorial imagery that carries its own weight. Its
              CHILDREN are excluded from the cast — a caption inside
              the frame belongs to the frame.
     BLOCK    a composed banner: takes the media role itself, but
              its children stay in the cast so the banner leads and
              the words inside it follow.
     ENSEMBLE the boxes: product cards, collection tiles, feature
              cards, stats, testimonials, budget tiles.

     `.step` is deliberately absent: [data-steps] in ths3.js already
     runs a designed sequence and two schedules on one element
     cancel each other out.
     `.ths3-hswrap` (the slideshow) is skipped whole — it ships its
     own per-slide stagger, which is already a sequence. */
  var LEAD = '.eyebrow, .reel, .h1, .h2, h1, h2, .lede, .cta-row, .chips, [data-filterbar]';
  var MEDIA = '.split__visual, .aplus__media';
  var BLOCK = '.hero-banner';
  var ENS = '.card, .ct, .feat, .gk__card, .stat, .tq, .val, .cs, .qcard, .bt, .byo__step, .tcard';
  var SKIP = 'header, footer, nav, dialog, .drawer, cart-drawer, .ths-toast, .ths3-hswrap';

  function vh() { return window.innerHeight || root.clientHeight; }
  function inScope(el) { return !el.closest(SKIP); }

  var sceneIO = null;
  var itemIO = null;
  var rowIO = null;
  var pending = [];          /* everything left hidden, for the sweep */

  /* A sideways swipe row clips its own children out of every
     intersection test, so its cards have to be revealed by the row.
     Cached per parent — one getComputedStyle per container, not one
     per card. */
  function scrollerOf(el, scene) {
    var p = el.parentElement;
    while (p && p !== scene && p !== doc.body) {
      if (p.__choScroll === undefined) {
        var ox = getComputedStyle(p).overflowX;
        p.__choScroll = (ox === 'auto' || ox === 'scroll') && p.scrollWidth > p.clientWidth + 4;
      }
      if (p.__choScroll) return p;
      p = p.parentElement;
    }
    return null;
  }

  /* ── headline word split ──────────────────────────────────────
     Moved here from ths3.js §8 so the words start on the SECTION's
     beat instead of on a threshold of their own. Produces exactly
     the .rw / .rw__i markup the existing CSS expects. */
  function splitWords(h) {
    if (h.__choSplit) return;
    h.__choSplit = 1;
    var tokens = [];
    (function walk(node, hl) {
      Array.prototype.forEach.call(node.childNodes, function (c) {
        if (c.nodeType === 3) {
          c.nodeValue.split(/(\s+)/).forEach(function (p) {
            if (p === '') return;
            tokens.push(/^\s+$/.test(p) ? { sp: true } : { w: p, hl: hl });
          });
        } else if (c.nodeType === 1) {
          walk(c, hl || (c.classList && c.classList.contains('hl')));
        }
      });
    })(h, false);
    if (!tokens.length) return;
    h.textContent = '';
    var words = [];
    tokens.forEach(function (t) {
      if (t.sp) { h.appendChild(doc.createTextNode(' ')); return; }
      var outer = doc.createElement('span'); outer.className = 'rw';
      var inner = doc.createElement('span'); inner.className = 'rw__i' + (t.hl ? ' hl' : '');
      inner.textContent = t.w;
      outer.appendChild(inner); h.appendChild(outer);
      words.push(inner);
    });
    h.__choWords = words;
  }

  function runWords(h, base) {
    var words = h.__choWords;
    if (words) {
      for (var i = 0; i < words.length; i++) {
        words[i].style.transitionDelay = (base + Math.min(i * 42, 620)) + 'ms';
      }
    }
    h.classList.add('reveal-in');
  }

  /* A grid revealed in flat DOM order reads as a list being
     printed. Cascading by COLUMN within each visual row gives the
     diagonal sweep that makes a grid look assembled rather than
     dealt out. The row map is built inline in prepare(), from a
     single batched pass of rect reads. */

  /* ══ per-section preparation ══════════════════════════════════ */
  function prepare(scene) {
    if (scene.__cho || !inScope(scene)) return;
    scene.__cho = 1;

    var i, el;
    var fold = vh() * 0.92;
    var above = scene.getBoundingClientRect().top < fold;

    /* ONE sequence in document order. Document order IS the
       choreography: whatever the section says first, moves first. */
    var seq = [];
    var nodes = scene.querySelectorAll(LEAD + ', ' + MEDIA + ', ' + BLOCK);
    for (i = 0; i < nodes.length; i++) {
      el = nodes[i];
      if (!inScope(el) || el.closest(ENS)) continue;
      var isMedia = el.matches(MEDIA) || el.matches(BLOCK);
      /* children of a framed media element belong to the frame */
      if (!isMedia && el.closest(MEDIA)) continue;
      var hadRv = el.classList.contains('rv');
      var isWords = el.hasAttribute('data-reveal');
      /* Above the fold, only things the markup ALREADY hides may
         animate. Anything else is visible at first paint and would
         have to be hidden to be animated — a flash of disappearing
         content is worse than no animation. */
      if (above && !hadRv && !isWords) continue;
      el.__choClaimed = 1;
      el.classList.add(isMedia ? 'rv--media' : 'rv--lead');
      if (isWords) splitWords(el);
      else el.classList.add('rv');
      seq.push({ el: el, d: Math.min(seq.length * 82, 400) });
    }
    scene.__choSeq = seq;

    /* ── ENSEMBLE: each box triggers on its own row ──
       A 24-card grid is four viewports tall. Cascading it off the
       section trigger would animate most of it while it is still
       off screen, so by the time it is scrolled to it has already
       finished. Per-item triggering keeps every row arriving as it
       is reached; the column index keeps the row itself diagonal. */
    /* Read every rect in ONE pass before writing a single class.
       Interleaving `getBoundingClientRect()` with `classList.add()`
       forces a fresh layout per card, which on a 50-card collection
       page is 50 synchronous layouts on first paint. */
    var cand = [];
    nodes = scene.querySelectorAll(ENS);
    for (i = 0; i < nodes.length; i++) {
      el = nodes[i];
      if (!inScope(el) || el.closest(MEDIA)) continue;
      cand.push(el);
    }
    var tops = new Array(cand.length);
    for (i = 0; i < cand.length; i++) tops[i] = cand[i].getBoundingClientRect().top;

    var ens = [], ensTop = [];
    var rowTop = null, col = 0;
    for (i = 0; i < cand.length; i++) {
      /* column index for the diagonal sweep, from the row map of ALL
         candidates so a skipped card cannot shift the columns */
      var t = Math.round(tops[i]);
      if (rowTop === null || Math.abs(t - rowTop) > 14) { rowTop = t; col = 0; }
      cand[i].__choCol = col++;
      if (above && !cand[i].classList.contains('rv') && tops[i] < fold) continue;
      cand[i].__choClaimed = 1;
      cand[i].classList.add('rv', 'rv--ens');
      ens.push(cand[i]);
      ensTop.push(tops[i]);
    }

    if (reduced || !hasIO) {
      revealScene(scene, true);
      for (i = 0; i < ens.length; i++) ens[i].classList.add('in');
      return;
    }

    if (above) {
      /* on screen at first paint: it was already hidden by the
         markup, so play the sequence — two frames later, so the
         browser has painted the start state and the transition
         actually runs */
      raf2(function () { revealScene(scene, false); });
    } else {
      sceneIO.observe(scene);
      pending.push(scene);
    }

    var rows = [];
    for (i = 0; i < ens.length; i++) {
      if (ensTop[i] < fold) {
        (function (item) { raf2(function () { revealItem(item); }); })(ens[i]);
        continue;
      }
      var row = scrollerOf(ens[i], scene);
      if (row) {
        if (!row.__choItems) { row.__choItems = []; rows.push(row); }
        row.__choItems.push(ens[i]);
      } else {
        itemIO.observe(ens[i]);
      }
      pending.push(ens[i]);
    }
    for (i = 0; i < rows.length; i++) rowIO.observe(rows[i]);

    collectDrift(scene);
  }

  function raf2(fn) {
    requestAnimationFrame(function () { requestAnimationFrame(fn); });
  }

  /* instant = no stagger. Used in reduced motion, in the editor and
     by the safety sweep. */
  function revealScene(scene, instant) {
    var seq = scene.__choSeq || [];
    for (var i = 0; i < seq.length; i++) {
      var el = seq[i].el;
      var d = instant ? 0 : seq[i].d;
      if (!instant) el.style.setProperty('--cho-d', d + 'ms');
      if (el.hasAttribute('data-reveal')) runWords(el, d);
      el.classList.add('in');
    }
    scene.classList.add('cho-in');
  }

  function revealItem(el) {
    el.style.setProperty('--cho-d', (90 + Math.min(el.__choCol || 0, 5) * 66) + 'ms');
    el.classList.add('in');
  }

  /* ══ scroll-linked drift ══════════════════════════════════════
     An entrance is over in a second; after that a page is static
     again. A small, continuous, scroll-POSITION-linked drift is
     what keeps it alive under the thumb.

     Copy and photography move in OPPOSITE directions as a section
     crosses the viewport, so they read as two related things
     rather than one printed board. Amplitudes are ≤13px on
     purpose: enough to feel, never enough to look misaligned. No
     layout property is touched, so nothing can reflow.

     Photography drifts with the `translate` property (its
     `transform` is the zoom settle); copy drifts with `transform`
     (its entrance uses `translate`). Neither property is ever
     written by two systems. */
  var drift = [];
  var driftLive = [];
  var driftIO = null;
  var driftTicking = false;

  function collectDrift(scene) {
    if (reduced) return;
    var i, el;
    var imgs = scene.querySelectorAll('.ct__media img, .split__visual img, .aplus__media img');
    for (i = 0; i < imgs.length; i++) {
      el = imgs[i];
      if (!inScope(el) || el.__choDrift) continue;
      el.setAttribute('data-cho-drift-media', '');
      drift.push((el.__choDrift = { el: el, amp: -13, media: true }));
    }
    /* the section's voice only — not the CTA row, which sits beside
       the grid and would look detached if it floated */
    var voice = scene.querySelectorAll('.eyebrow, .reel, .h1, .h2, h1, h2, .lede');
    for (i = 0; i < voice.length; i++) {
      el = voice[i];
      if (!inScope(el) || el.__choDrift || el.closest(ENS) || el.closest(MEDIA)) continue;
      el.setAttribute('data-cho-drift', '');
      drift.push((el.__choDrift = { el: el, amp: 9, media: false }));
    }
    if (!drift.length) return;
    if (!driftIO) startDrift();
    else for (i = 0; i < drift.length; i++) observeDrift(drift[i]);
  }

  function observeDrift(d) {
    if (d.obs) return;
    d.obs = 1;
    driftIO.observe(d.el);
  }

  function startDrift() {
    driftIO = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var d = entries[i].target.__choDrift;
        if (!d) continue;
        var at = driftLive.indexOf(d);
        if (entries[i].isIntersecting) {
          if (at === -1) { driftLive.push(d); d.el.classList.add('cho-drifting'); }
        } else if (at !== -1) {
          driftLive.splice(at, 1);
          d.el.classList.remove('cho-drifting');
          if (d.media) d.el.style.translate = '';
          else d.el.style.transform = '';
        }
      }
      if (driftLive.length) tick();
    }, { rootMargin: '10% 0px 10% 0px' });

    for (var i = 0; i < drift.length; i++) observeDrift(drift[i]);
    window.addEventListener('scroll', onDriftScroll, { passive: true });
    window.addEventListener('resize', onDriftScroll, { passive: true });
  }

  function onDriftScroll() {
    if (driftTicking || !driftLive.length) return;
    driftTicking = true;
    requestAnimationFrame(tick);
  }

  function tick() {
    driftTicking = false;
    var h = vh();
    var half = h / 2;
    var amp = h < 700 ? 0.6 : 1;            /* gentler on phones */
    var n = driftLive.length;
    var p = new Array(n);
    var i, v;
    /* read every rect first, then write — never interleaved */
    for (i = 0; i < n; i++) {
      var r = driftLive[i].el.getBoundingClientRect();
      v = (half - (r.top + r.height / 2)) / h;
      p[i] = v > 1 ? 1 : (v < -1 ? -1 : v);
    }
    for (i = 0; i < n; i++) {
      var d = driftLive[i];
      var y = (p[i] * d.amp * amp).toFixed(1);
      if (d.media) d.el.style.translate = '0 ' + y + 'px';
      else d.el.style.transform = 'translate3d(0,' + y + 'px,0)';
    }
  }

  /* ══ strays ═══════════════════════════════════════════════════
     Some sections author .rv in markup on elements the cast lists
     do not name (cart rows, facets, the hero banner wrapper).
     Those are hidden by CSS, so something must always reveal them
     — an unclaimed .rv is an invisible section. */
  function adoptStrays(scope) {
    var strays = scope.querySelectorAll('.rv:not(.in)');
    var fold = vh() * 0.92;
    for (var i = 0; i < strays.length; i++) {
      var el = strays[i];
      if (el.__choClaimed || el.__choStray) continue;
      el.__choStray = 1;
      if (reduced || !hasIO) { el.classList.add('in'); continue; }
      el.classList.add('rv--lead');
      if (el.getBoundingClientRect().top < fold) {
        (function (n) { raf2(function () { n.classList.add('in'); }); })(el);
        continue;
      }
      var row = scrollerOf(el, null);
      if (row) {
        if (!row.__choItems) { row.__choItems = []; rowIO.observe(row); }
        row.__choItems.push(el);
      } else {
        itemIO.observe(el);
      }
      pending.push(el);
    }
  }

  /* ══ boot ═════════════════════════════════════════════════════ */
  function start(scope) {
    scope = scope || doc;

    if (!sceneIO && hasIO && !reduced) {
      /* -6% only: the trigger has to fire while the section is
         still mostly below the fold, so its heading is already
         moving by the time the shopper reaches it */
      sceneIO = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          revealScene(entries[i].target, false);
          sceneIO.unobserve(entries[i].target);
        }
      }, { threshold: 0.01, rootMargin: '0px 0px -6% 0px' });

      itemIO = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          revealItem(entries[i].target);
          itemIO.unobserve(entries[i].target);
        }
      }, { threshold: 0.06, rootMargin: '0px 0px -8% 0px' });

      /* Rows that scroll sideways are observed as a ROW, never as
         individual cards. An IntersectionObserver clips the
         intersection through every ancestor's overflow box, so a
         card sitting at x=1291 inside a 390px-wide
         `overflow-x: auto` swipe row NEVER intersects the viewport
         — no rootMargin can reach past an ancestor clip. Measured
         in Chrome at 390px: 11 cards and feature tiles on the
         homepage were still at opacity 0 after a full scroll to the
         bottom, waiting to be swiped into as blank space.

         Revealing per row is also the better effect: the row
         cascades in as one diagonal sweep and a sideways swipe
         never uncovers anything unfinished. */
      rowIO = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          var row = entries[i].target;
          rowIO.unobserve(row);
          var items = row.__choItems || [];
          for (var j = 0; j < items.length; j++) revealItem(items[j]);
        }
      }, { threshold: 0.02, rootMargin: '0px 0px -8% 0px' });
    }

    var scenes = scope.querySelectorAll('.scene, [data-choreo]');
    for (var i = 0; i < scenes.length; i++) prepare(scenes[i]);
    adoptStrays(scope);
  }

  /* Safety net: nothing this script touched may stay invisible.
     Runs twice — once after the first images have settled the
     layout, once well after any slow third-party embed. */
  function sweep() {
    var i;
    for (i = 0; i < pending.length; i++) {
      var el = pending[i];
      if (el.classList.contains('in') || el.classList.contains('cho-in')) continue;
      if (el.getBoundingClientRect().top > vh()) continue;   /* still below — leave it its entrance */
      if (el.__choSeq) revealScene(el, true);
      else el.classList.add('in');
    }
    var strays = doc.querySelectorAll('.rv:not(.in)');
    for (i = 0; i < strays.length; i++) {
      if (strays[i].getBoundingClientRect().top < vh()) strays[i].classList.add('in');
    }
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { start(); });
  else start();

  setTimeout(sweep, 3000);
  setTimeout(sweep, 7000);

  /* Theme editor: sections re-render with no page load. Prepare the
     new markup and reveal it immediately — Manish must never see a
     blank band while he edits. */
  doc.addEventListener('shopify:section:load', function (ev) {
    var target = ev.target || doc;
    start(target);
    var all = target.querySelectorAll('.rv, .scene, [data-reveal]');
    for (var i = 0; i < all.length; i++) {
      if (all[i].__choSeq) revealScene(all[i], true);
      all[i].classList.add('in');
      if (all[i].hasAttribute('data-reveal')) all[i].classList.add('reveal-in');
    }
  });

  /* bfcache restore reuses the DOM, so anything revealed stays
     revealed — just put the drift back in step with the scroll
     position the page came back to. */
  window.addEventListener('pageshow', function (e) { if (e.persisted) onDriftScroll(); });
})();
